/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { PlusIcon, ChevronRightIcon } from '@/shared/ui/radix-icons';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import {
  BRAND_COLOR,
  calendarColor,
  calendarDisplayName,
} from '@features/agenda/utils/calendar-display';
import { Text, UnstyledButton, InlineSpinner } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { GroupLabel } from './GroupLabel';
import {
  CalendarRow,
  CalendarRowEditor,
  CalendarSwatch,
  FixedBadge,
} from './CalendarSettingsDrawer.calendarRow';

type Translate = (key: string, vars?: Record<string, string>) => string;

/** A bordered card holding the rows of one group. */
const GroupCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="divide-y divide-[var(--border-subtle)] overflow-hidden rounded-(--radius-callout) border border-[var(--border-subtle)] bg-[var(--surface-primary)]">
    {children}
  </div>
);

export interface AgendasPaneProps {
  calendars: Calendar[];
  institutional?: Calendar;
  personal: Calendar[];
  googleCalendars: Calendar[];
  /** Whether the Google integration is available at all. */
  showGoogle: boolean;
  googleConnected: boolean;
  googleWritable: boolean;
  agencyName?: string;
  loading: boolean;
  /** Calendar being renamed, or 'new' while the create editor is open. */
  editingId: string | null;
  draftName: string;
  draftColor: string;
  isSaving: boolean;
  t: Translate;
  onStartCreate: () => void;
  onStartEdit: (calendar: Calendar) => void;
  onDraftNameChange: (name: string) => void;
  onDraftColorChange: (color: string) => void;
  onSubmitDraft: () => void;
  onCancelDraft: () => void;
  onDelete: (calendarId: string) => void;
  onGoToGoogle: () => void;
}

/**
 * The "Agendas" section of the calendar settings.
 *
 * The three groups answer the question the old flat list could not: who else
 * sees this calendar. Institutional is the agency's and cannot be touched,
 * personal ones belong to the signed-in user, and the Google ones are visible
 * to nobody but them.
 *
 * @param props - Calendars, draft state and handlers.
 * @returns The rendered pane.
 */
export const AgendasPane: React.FC<AgendasPaneProps> = ({
  institutional,
  personal,
  googleCalendars,
  showGoogle,
  googleConnected,
  googleWritable,
  agencyName,
  loading,
  editingId,
  draftName,
  draftColor,
  isSaving,
  t,
  onStartCreate,
  onStartEdit,
  onDraftNameChange,
  onDraftColorChange,
  onSubmitDraft,
  onCancelDraft,
  onDelete,
  onGoToGoogle,
}) => (
  <section
    role="tabpanel"
    aria-label={t('calendar_settings.nav_calendars')}
    className="flex flex-col gap-(--space-6)"
  >
    <header className="flex flex-col gap-(--space-1)">
      <Text
        component="h2"
        className="m-0 text-[length:var(--text-app-section)] font-[weight:var(--font-semibold)] text-[var(--text-primary)]"
      >
        {t('calendar_settings.nav_calendars')}
      </Text>
      <Text className="text-[length:var(--text-small)] text-[var(--text-secondary)] text-pretty">
        {t('calendar_settings.calendars_subtitle')}
      </Text>
    </header>

    {loading && <InlineSpinner />}

    {institutional && (
      <div className="flex flex-col gap-(--space-2)">
        <GroupLabel>{t('calendar_settings.group_institutional')}</GroupLabel>
        <GroupCard>
          <CalendarRow
            name={calendarDisplayName(institutional, t, agencyName)}
            color={BRAND_COLOR}
            meta={t('calendar_settings.institutional_meta')}
            badge={<FixedBadge label={t('calendar_settings.badge_fixed')} />}
            t={t}
          />
        </GroupCard>
      </div>
    )}

    <div className="flex flex-col gap-(--space-2)">
      <div className="flex items-center justify-between gap-(--space-3)">
        <GroupLabel>{t('calendar_settings.group_personal')}</GroupLabel>
        {editingId === null && (
          <UnstyledButton
            type="button"
            onClick={onStartCreate}
            className="flex items-center gap-(--space-1) text-[length:var(--text-small)] font-[weight:var(--font-semibold)] text-[var(--link-text)] hover:underline"
          >
            <PlusIcon width={12} height={12} aria-hidden="true" />
            {t('calendar_settings.new_calendar')}
          </UnstyledButton>
        )}
      </div>
      <GroupCard>
        {editingId === 'new' && (
          <CalendarRowEditor
            name={draftName}
            color={draftColor}
            submitLabel={t('calendar_settings.create_calendar')}
            saving={isSaving}
            t={t}
            onNameChange={onDraftNameChange}
            onColorChange={onDraftColorChange}
            onSubmit={onSubmitDraft}
            onCancel={onCancelDraft}
          />
        )}
        {personal.map((calendar) =>
          editingId === calendar.calendar_id ? (
            <CalendarRowEditor
              key={calendar.calendar_id}
              name={draftName}
              color={draftColor}
              submitLabel={t('actions.save')}
              saving={isSaving}
              t={t}
              onNameChange={onDraftNameChange}
              onColorChange={onDraftColorChange}
              onSubmit={onSubmitDraft}
              onCancel={onCancelDraft}
            />
          ) : (
            <CalendarRow
              key={calendar.calendar_id}
              name={calendarDisplayName(calendar, t, agencyName)}
              color={calendarColor(calendar)}
              badge={
                googleWritable && calendar.sync?.google?.push_enabled ? (
                  <Text
                    component="span"
                    className="shrink-0 rounded-(--radius-sm) bg-[var(--surface-tertiary)] px-(--space-2) py-(--space-0-5) text-[length:var(--text-tag)] font-[weight:var(--font-medium)] text-[var(--text-tertiary)]"
                  >
                    {t('calendar_settings.badge_saved_google')}
                  </Text>
                ) : undefined
              }
              t={t}
              onEdit={() => onStartEdit(calendar)}
              onDelete={() => onDelete(calendar.calendar_id ?? '')}
            />
          ),
        )}
        {personal.length === 0 && editingId !== 'new' && (
          <Text className="block px-(--space-3) py-(--space-4) text-[length:var(--text-small)] text-[var(--text-secondary)]">
            {t('calendar_settings.personal_empty')}
          </Text>
        )}
      </GroupCard>
    </div>

    {showGoogle && (
      <div className="flex flex-col gap-(--space-2)">
        <GroupLabel>{t('calendar_settings.group_google')}</GroupLabel>
        {googleConnected ? (
          <GroupCard>
            {/*
              Editable like any other row: the name and the colour are how
              the calendar reads inside Housien, not data owned by Google.
              No delete, though — leaving Google is the import switch, not a
              deletion that would come back on the next sync.
            */}
            {googleCalendars.map((calendar) =>
              editingId === calendar.calendar_id ? (
                <CalendarRowEditor
                  key={calendar.calendar_id}
                  name={draftName}
                  color={draftColor}
                  submitLabel={t('actions.save')}
                  saving={isSaving}
                  t={t}
                  onNameChange={onDraftNameChange}
                  onColorChange={onDraftColorChange}
                  onSubmit={onSubmitDraft}
                  onCancel={onCancelDraft}
                />
              ) : (
                <CalendarRow
                  key={calendar.calendar_id}
                  name={calendarDisplayName(calendar, t, agencyName)}
                  color={calendarColor(calendar)}
                  external
                  meta={
                    googleWritable
                      ? t('calendar_settings.access_read_write')
                      : t('calendar_settings.access_read')
                  }
                  t={t}
                  onEdit={() => onStartEdit(calendar)}
                />
              ),
            )}
            <UnstyledButton
              type="button"
              onClick={onGoToGoogle}
              className="flex w-full items-center justify-between gap-(--space-2) px-(--space-3) py-(--space-2-5) text-[length:var(--text-small)] font-[weight:var(--font-semibold)] text-[var(--link-text)] hover:bg-[var(--surface-hover)]"
            >
              {t('calendar_settings.choose_google_calendars')}
              <ChevronRightIcon width={14} height={14} aria-hidden="true" />
            </UnstyledButton>
          </GroupCard>
        ) : (
          <div
            className={cn(
              'flex flex-col items-start gap-(--space-3) rounded-(--radius-callout)',
              'border border-dashed border-[var(--border-dashed)] px-(--space-4) py-(--space-4)',
            )}
          >
            <Text className="text-[length:var(--text-small)] text-[var(--text-secondary)] text-pretty">
              {t('calendar_settings.google_empty')}
            </Text>
            <UnstyledButton
              type="button"
              onClick={onGoToGoogle}
              className="text-[length:var(--text-small)] font-[weight:var(--font-semibold)] text-[var(--link-text)] hover:underline"
            >
              {t('calendar_settings.google_empty_cta')}
            </UnstyledButton>
          </div>
        )}
      </div>
    )}
  </section>
);
